import { useEffect, useState } from "react";
import { fileUrl } from "../lib/api";

export function Countdown({ to, endLabel = "Event ended", liveUntil }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!to) return null;
  const target = new Date(to).getTime();
  const end = liveUntil ? new Date(liveUntil).getTime() : target;
  const diff = target - now;

  if (now >= target && now <= end) return <span className="inline-flex items-center gap-2 text-rose-400 font-bold"><span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" /> Live now</span>;
  if (now > end) return <span className="text-zinc-500 font-medium">{endLabel}</span>;

  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (d === 0 && h === 0 && m === 0 && diff > 0 && new Date(to).toDateString() === new Date(now).toDateString()) {
    // same day handled by units below; keep generic
  }
  const unit = (val, lbl) => (
    <div className="flex flex-col items-center">
      <span className="font-display font-black text-2xl md:text-3xl tabular-nums">{String(val).padStart(2, "0")}</span>
      <span className="text-[0.6rem] uppercase tracking-widest text-zinc-500">{lbl}</span>
    </div>
  );
  return (
    <div className="flex gap-4 md:gap-6" data-testid="countdown">
      {unit(d, "Days")}{unit(h, "Hrs")}{unit(m, "Min")}{unit(s, "Sec")}
    </div>
  );
}

export function Sponsors({ items, title = "Proudly sponsored by" }) {
  if (!items || items.length === 0) return null;
  return (
    <section data-testid="sponsors-section">
      <p className="overline mb-5">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {items.map((s) => (
          <a key={s.id} href={s.website || "#"} target="_blank" rel="noreferrer"
            className="group bg-[#111] border border-white/10 rounded-2xl p-5 flex flex-col items-center text-center card-hover" data-testid={`sponsor-${s.slug || s.id}`}>
            {s.logo ? <img src={fileUrl(s.logo)} alt={s.name} className="w-16 h-16 rounded-xl object-cover mb-3" /> : <div className="w-16 h-16 rounded-xl bg-white/5 mb-3" />}
            <p className="font-semibold text-sm">{s.name}</p>
            {s.level && <span className="mt-1 text-[0.6rem] uppercase tracking-widest font-bold text-rose-400" data-testid={`sponsor-level-${s.slug || s.id}`}>{s.level}</span>}
            {s.sponsor_type && <p className="text-xs text-zinc-500 mt-0.5">{s.sponsor_type}</p>}
            {s.featured && <span className="mt-2 text-[0.6rem] uppercase tracking-widest text-amber-400">Featured</span>}
          </a>
        ))}
      </div>
    </section>
  );
}

export function availabilityLabel(availability) {
  if (!availability || availability.length === 0) return null;
  const today = new Date().toISOString().slice(0, 10);
  const start = (a) => a.start_date || a.date || "";
  const end = (a) => a.end_date || a.start_date || a.date || "";
  const future = availability.filter((a) => end(a) >= today);
  const avail = future.filter((a) => a.status === "available");
  const booked = future.filter((a) => a.status === "booked");
  if (avail.length === 0 && booked.length > 0) return { text: "Currently booked", tone: "zinc" };
  if (avail.length === 0) return { text: "Contact for availability", tone: "zinc" };
  // check this weekend (next 7 days)
  const wk = new Date(); wk.setDate(wk.getDate() + 7);
  const wkStr = wk.toISOString().slice(0, 10);
  if (avail.some((a) => start(a) <= wkStr)) return { text: "Available this weekend", tone: "emerald" };
  if (avail.length <= 2) return { text: "Limited availability", tone: "amber" };
  return { text: "Available for booking", tone: "emerald" };
}
